import { useCallback, useEffect, useState } from "react";

import type { IssueProviderDetail } from "../types";

import {
  getIssueIntegrationProviderDetailAsUser,
  listIssueIntegrationProvidersAsUser,
} from "../dashboard-client";

export type JiraSetupViewState =
  | { status: "loading" }
  | { status: "not-installed"; connectUrl: string | null }
  | {
      status: "installed";
      detail: IssueProviderDetail;
      connectUrl: string | null;
    }
  | { status: "error"; message: string };

export function useJiraSetupView(): {
  view: JiraSetupViewState;
  retry: () => void;
} {
  const [view, setView] = useState<JiraSetupViewState>({ status: "loading" });

  const loadData = useCallback(async () => {
    setView({ status: "loading" });
    try {
      const { data: listData } = await listIssueIntegrationProvidersAsUser();
      const connectUrl = listData.connect_url ?? null;
      const jiraProvider = listData.providers.find((p) => p.type === "jira");

      if (!jiraProvider) {
        setView({ connectUrl, status: "not-installed" });
        return;
      }

      const { data: detail } = await getIssueIntegrationProviderDetailAsUser({
        path: { providerId: jiraProvider.provider_id },
      });
      setView({ connectUrl, detail, status: "installed" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load Jira status.";
      setView({ message, status: "error" });
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const retry = useCallback(() => {
    void loadData();
  }, [loadData]);

  return { retry, view };
}
